import {ReactNode, useContext, useEffect, useMemo} from 'react';
import {NetworkContext} from '../../lib/context';
import {observer} from 'mobx-react';
import './revision-id-selector.scss';
import NOFilter from '../no-filters/no-filter';
import {useNavigate} from 'react-router';
import {SkeletonPlaceholder} from '@carbon/react';

function groupAndSortByRosterId(data: any[], rosterId?: string) {
    let filteredData = [...data];
    if(rosterId) {
        filteredData = filteredData.filter(item => item.rosterId === rosterId);
    }
    // Group by rosterId
    const grouped = filteredData.reduce((acc, obj) => {
        if (!acc[obj.rosterId]) acc[obj.rosterId] = [];
        acc[obj.rosterId].push(obj);
        return acc;
    }, {});

    // Flatten and sort by revisionId within each group
    return Object.values(grouped)
        .map((group: any) =>
            group.sort((a: any, b: any) => Number(a.revisionId) - Number(b.revisionId))
        )
        .flat()
        .map((item: any) => ({
            revisionId: item.revisionId,
            revisionName: item.revisionName,
            rosterName: item.rosterName,
            originalVsAdded: item.revisionId === '0' ? 'Original' : 'Revised'
        }));
}
interface RevisionIdSelectorProps {
    rosterId?: string;
}

const RevisionIdSelector = observer(({rosterId}: RevisionIdSelectorProps) => {
    const networkOptimizationStore = useContext(NetworkContext);
    const navigate = useNavigate();

    useEffect(() => {
        if (networkOptimizationStore.revisionId === undefined || networkOptimizationStore.revisionId === null) {
            networkOptimizationStore.setRevisionId(0);
        }
    }, [networkOptimizationStore]);

    const revisionIdSelectorData = useMemo(() => {
        return groupAndSortByRosterId(networkOptimizationStore.providerRosters || [], rosterId);
    }, [networkOptimizationStore.providerRosters]);


    const revisionIdSelector = useMemo(() => {

        const onChange = (value: string) => {
            const newRevisionId = parseInt(value);
            networkOptimizationStore.setRevisionId(newRevisionId);
            
            // Update URL with new revisionId
            const pathParts = location.pathname.split('/');
            // Pattern: /network-optimization/:rosterId/:revisionId/:pageName
            if (pathParts.length === 5 && pathParts[1] === 'network-optimization') {
                const currentRosterId = pathParts[2];
                const pageName = pathParts[4];
                navigate(`/network-optimization/${currentRosterId}/${newRevisionId}/${pageName}`, {replace: true});
            }
        };

        const children: [(item: any, i: number) => string, (item: any, i: number) => ReactNode] = [
            // Return the value as string for identification
            (item: any): string => {
                return item.revisionId.toString();
            },
            // Render the display content
            (item: any): ReactNode => {
                return (
                    `${item.originalVsAdded} | ${item.rosterName}${item.revisionId !== '0' ? ` - ${item.revisionName}` : ''}`
                );
            }
        ];


        return {
            label: '',
            data: revisionIdSelectorData,
            value: (networkOptimizationStore.revisionId ?? 0).toString(),
            onChange,
            children,
            spinner: false,
            disabled: false,
        };
    }, [networkOptimizationStore.revisionId, networkOptimizationStore.providerRosters, revisionIdSelectorData]);

    if(networkOptimizationStore.providerRosters.length === 0)
        return <SkeletonPlaceholder className='revision-id-selector-skeleton'/>;

    return (
        <div className='revision-id-selector'>
            <NOFilter
                searchable
                required
                data-testid='roster-revision-id-dropdown'
                data={revisionIdSelector.data}
                value={revisionIdSelector.value ?? revisionIdSelector.data[0].revisionId.toString()}
                onChange={revisionIdSelector.onChange}
                disabled={revisionIdSelector.disabled}
                itemToString={(item) => `${item.originalVsAdded} | ${item.rosterName}${item.revisionId !== '0' ? ` - ${item.revisionName}` : ''}`}
            >
                {revisionIdSelector.children}
            </NOFilter>
        </div>
    );
});

export default RevisionIdSelector;